import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Grid,
  IconButton,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  FileCopy as CopyIcon,
  TextFields as TextIcon,
  Image as ImageIcon,
  AudioFile as AudioIcon,
  VideoFile as VideoIcon,
  PictureAsPdf as PdfIcon,
  DataObject as JsonIcon,
  AttachFile as FileIcon,
  Preview as PreviewIcon,
} from '@mui/icons-material';
import {
  DataTable,
  PageHeader,
  type Column,
  type SortDirection,
} from './common';

interface TestMaterial {
  id: string;
  name: string;
  description?: string;
  data: {
    content_type: string;
    content?: string;
    file_url?: string;
    file_key?: string;
    file_name?: string;
    file_size?: number;
    metadata?: any;
  };
  linked_functions?: string[];
  created_at: string;
  is_active: boolean;
}

const contentTypeIcons: Record<string, React.ReactNode> = {
  text: <TextIcon />,
  json: <JsonIcon />,
  image: <ImageIcon />,
  audio: <AudioIcon />,
  video: <VideoIcon />,
  pdf: <PdfIcon />,
  file: <FileIcon />,
};

// Safeguard constants for test materials
const MAX_FILE_SIZE_BYTES = 500 * 1024 * 1024; // 500 MB
const MAX_TEST_MATERIALS_COUNT = 100; // Maximum number of test materials per customer
const MAX_FILE_SIZE_DISPLAY = '500 MB';

export const TestMaterials: React.FC = () => {
  const { user } = useAuth();
  const [materials, setMaterials] = useState<TestMaterial[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState<TestMaterial | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    content_type: 'text',
    content: '',
    file: null as File | null,
  });
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<string>('created_at');
  const [sortDir, setSortDir] = useState<SortDirection>('desc');

  useEffect(() => {
    if (user) {
      loadTestMaterials();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, page, rowsPerPage, sortBy, sortDir]);

  const loadTestMaterials = async () => {
    try {
      setLoading(true);
      const idToken = await user?.getIdToken();
      
      let url = `/api/v2/test-materials/?page=${page + 1}&page_size=${rowsPerPage}&sort_by=${sortBy}&sort_dir=${sortDir}`;
      if (searchTerm) {
        url += `&search=${encodeURIComponent(searchTerm)}`;
      }
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${idToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();

        const materialsList = data.results || data.data || [];

        
        
        setMaterials(materialsList);
        setTotalCount(data.pagination?.total_items || 0);
      } else {
        setError('Failed to load test materials');
      }
    } catch (err) {

      setError('Error loading test materials');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      // Check if customer has reached maximum test materials limit
      if (totalCount >= MAX_TEST_MATERIALS_COUNT) {
        setError(`Maximum test materials limit (${MAX_TEST_MATERIALS_COUNT}) reached. Please delete some test materials before creating new ones.`);
        return;
      }
      
      const idToken = await user?.getIdToken();
      
      // Check if it's a file upload or text/json content
      if (['image', 'audio', 'video', 'pdf', 'file'].includes(formData.content_type) && formData.file) {
        // File upload
        const uploadFormData = new FormData();
        uploadFormData.append('name', formData.name);
        uploadFormData.append('description', formData.description || '');
        uploadFormData.append('content_type', formData.content_type);
        uploadFormData.append('file', formData.file);
        
        const response = await fetch('/api/v2/test-materials/upload', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${idToken}`,
          },
          body: uploadFormData,
        });

        if (response.ok) {
          const result = await response.json();
          const createdMaterial = result.data || result;
          
          // Show a message if the name was auto-modified
          if (createdMaterial.name !== formData.name) {

          }
          
          // Log storage location

          if (createdMaterial.data?.file_url) {

            alert(`File uploaded successfully to cloud storage!\n\nYou can preview it by clicking on the file in the list.`);
          } else if (createdMaterial.data?.content?.startsWith('data:')) {

          }
          
          await loadTestMaterials();
          handleCloseDialog();
        } else {
          const error = await response.json();

          setError(error.detail || 'Failed to upload test material');
        }
      } else {
        // Text/JSON content
        const payload = {
          name: formData.name,
          description: formData.description,
          data: {
            content_type: formData.content_type,
            content: formData.content,
          },
        };

        const response = await fetch('/api/v2/test-materials/', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        if (response.ok) {
          const result = await response.json();
          const createdMaterial = result.data || result;
          
          // Show a message if the name was auto-modified
          if (createdMaterial.name !== formData.name) {

          }
          
          await loadTestMaterials();
          handleCloseDialog();
        } else {
          const error = await response.json();
          setError(error.detail || 'Failed to create test material');
        }
      }
    } catch (err) {

      setError('Error creating test material');
    }
  };

  const handleUpdate = async () => {
    if (!editingMaterial) return;

    try {
      const idToken = await user?.getIdToken();
      
      const payload = {
        name: formData.name,
        description: formData.description,
        data: {
          content_type: formData.content_type,
          content: formData.content,
        },
      };

      const response = await fetch(`/api/v2/test-materials/${editingMaterial.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        await loadTestMaterials();
        handleCloseDialog();
      } else {
        const error = await response.json();
        setError(error.detail || 'Failed to update test material');
      }
    } catch (err) {

      setError('Error updating test material');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this test material?')) {
      return;
    }

    try {
      const idToken = await user?.getIdToken();
      const response = await fetch(`/api/v2/test-materials/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${idToken}`,
        },
      });

      if (response.ok) {
        await loadTestMaterials();
      } else {
        setError('Failed to delete test material');
      }
    } catch (err) {

      setError('Error deleting test material');
    }
  };

  const handleCloseDialog = () => {
    setShowCreateDialog(false);
    setEditingMaterial(null);
    setFormData({
      name: '',
      description: '',
      content_type: 'text',
      content: '',
      file: null,
    });
    setError(null);
  };

  const handleEdit = (material: TestMaterial) => {
    setEditingMaterial(material);
    setFormData({
      name: material.name,
      description: material.description || '',
      content_type: material.data.content_type,
      content: material.data.content || '',
      file: null,
    });
    setShowCreateDialog(true);
  };

  const getContentPreview = (material: TestMaterial) => {
    const content = material.data.content;
    
    // Check if it's a file type with base64 data or URL
    if (['image', 'audio', 'video', 'pdf', 'file'].includes(material.data.content_type)) {
      if (material.data.file_name) {
        const size = material.data.file_size ? ` (${(material.data.file_size / 1024 / 1024).toFixed(2)} MB)` : '';
        if (material.data.file_url) {
          return `📁 ${material.data.file_name}${size} [Cloud Storage]`;
        }
        return `File: ${material.data.file_name}${size}`;
      }
      if (content && content.startsWith('data:')) {
        return 'Base64 encoded file';
      }
      if (content && content.startsWith('http')) {
        return 'Stored in cloud storage';
      }
      return 'File content';
    }
    
    if (!content) return 'No content';
    
    if (material.data.content_type === 'json') {
      try {
        return JSON.stringify(JSON.parse(content), null, 2).substring(0, 100) + '...';
      } catch {
        return content.substring(0, 100) + '...';
      }
    }
    
    return content.substring(0, 100) + (content.length > 100 ? '...' : '');
  };

  const handlePreview = async (material: TestMaterial) => {


    
    // For file types, open in new tab
    if (['image', 'audio', 'video', 'pdf', 'file'].includes(material.data.content_type)) {
      const content = material.data.content;





      
      // Base64 data URL - open directly
      if (content && content.startsWith('data:')) {

        window.open(content, '_blank');
      }
      // Public URL from GCS - fetch via proxy to get proper blob URL
      else if (material.data.file_url) {

        try {
          const idToken = await user?.getIdToken();
          const response = await fetch(`/api/v2/test-materials/${material.id}/file`, {
            headers: {
              'Authorization': `Bearer ${idToken}`
            }
          });
          
          if (!response.ok) {
            throw new Error(`Failed to fetch file: ${response.statusText}`);
          }
          
          // Create blob URL for preview
          const blob = await response.blob();
          const blobUrl = URL.createObjectURL(blob);
          window.open(blobUrl, '_blank');
          
          // Clean up blob URL after a delay
          setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
        } catch (error) {

          alert('Unable to preview file: ' + (error instanceof Error ? error.message : 'Unknown error'));
        }
      }
      // Try to generate signed URL if we have file_key
      else if (material.data.file_key) {
        // TODO: Call API to get signed URL

        alert('File preview not available yet. File is stored but preview needs signed URL implementation.');
      }
      else {

        alert('Unable to preview this file. No content found.');
      }
    }
  };

  const handleSearch = () => {
    setPage(0);
    loadTestMaterials();
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortDir('asc');
    }
    setPage(0);
  };

  const handleCopy = async (material: TestMaterial) => {
    try {
      await navigator.clipboard.writeText(material.data.content || '');
      // You could add a success snackbar here
    } catch (err) {

    }
  };

  const columns: Column<TestMaterial>[] = [
    {
      id: 'name',
      label: 'Name',
      sortable: true,
      render: (value, row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {contentTypeIcons[row.data.content_type] || <TextIcon />}
          <Typography variant="body1" fontWeight="medium">
            {value}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'content_type',
      label: 'Type',
      sortable: true,
      render: (_, row) => (
        <Chip 
          label={row.data.content_type} 
          size="small"
          color="primary"
          variant="outlined"
        />
      ),
    },
    {
      id: 'description',
      label: 'Description',
      sortable: false,
      render: (value) => (
        <Typography variant="body2" color="textSecondary" sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {value || 'No description'}
        </Typography>
      ),
    },
    {
      id: 'preview',
      label: 'Preview',
      sortable: false,
      render: (_, row) => {
        const isFile = ['image', 'audio', 'video', 'pdf', 'file'].includes(row.data.content_type) && 
                      (row.data.file_url || row.data.content?.startsWith('data:') || row.data.file_key);
        
        return isFile ? (
          <Typography 
            variant="body2" 
            sx={{ 
              fontFamily: 'monospace', 
              fontSize: '0.875rem', 
              maxWidth: 200, 
              overflow: 'hidden', 
              textOverflow: 'ellipsis', 
              whiteSpace: 'nowrap',
              cursor: 'pointer',
              color: 'primary.main',
              textDecoration: 'underline',
              '&:hover': {
                color: 'primary.dark',
              }
            }}
            onClick={() => handlePreview(row)}
          >
            {getContentPreview(row)}
          </Typography>
        ) : (
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.875rem', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {getContentPreview(row)}
          </Typography>
        );
      },
    },
    {
      id: 'created_at',
      label: 'Created',
      sortable: true,
      render: (value) => new Date(value).toLocaleDateString(),
    },
    {
      id: 'actions',
      label: 'Actions',
      sortable: false,
      align: 'center',
      render: (_, row) => (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
          {['image', 'audio', 'video', 'pdf', 'file'].includes(row.data.content_type) && 
            (row.data.file_url || row.data.content?.startsWith('data:') || row.data.file_key) && (
            <Tooltip title="Preview">
              <IconButton
                size="small"
                onClick={() => handlePreview(row)}
                color="info"
              >
                <PreviewIcon />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => handleEdit(row)}
              color="primary"
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Copy Content">
            <IconButton
              size="small"
              onClick={() => handleCopy(row)}
              color="default"
            >
              <CopyIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={() => handleDelete(row.id)}
              color="error"
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];




  return (
    <Box>
      <Box>
        <PageHeader
          title="📝 Test Materials Manager"
          subtitle="Manage test data for your AI functions"
          actions={[
            {
              label: 'Create Test Material',
              onClick: () => {
                if (totalCount >= MAX_TEST_MATERIALS_COUNT) {
                  setError(`Maximum test materials limit (${MAX_TEST_MATERIALS_COUNT}) reached. Please delete some test materials first.`);
                  return;
                }
                setShowCreateDialog(true);
              },
              icon: <AddIcon />,
              variant: 'contained',
              disabled: totalCount >= MAX_TEST_MATERIALS_COUNT,
            },
          ]}
          searchValue={searchTerm}
          onSearchChange={setSearchTerm}
          onSearch={handleSearch}
          searchPlaceholder="Search test materials..."
          showSearch
        />
        <Box sx={{ display: 'flex', gap: 2, mb: 2, mt: -1 }}>
          <Chip 
            label={`${totalCount} / ${MAX_TEST_MATERIALS_COUNT} test materials`} 
            size="small" 
            color={totalCount >= MAX_TEST_MATERIALS_COUNT ? 'error' : totalCount >= MAX_TEST_MATERIALS_COUNT * 0.8 ? 'warning' : 'default'}
            variant="outlined"
          />
          <Chip 
            label={`Max file size: ${MAX_FILE_SIZE_DISPLAY}`} 
            size="small" 
            variant="outlined"
          />
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent sx={{ p: 0 }}>
          <DataTable
            columns={columns}
            data={materials}
            loading={loading}
            page={page}
            rowsPerPage={rowsPerPage}
            totalCount={totalCount}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            sortBy={sortBy}
            sortDirection={sortDir}
            onSort={handleSort}
            rowKey="id"
            emptyMessage="No test materials found. Create one to get started!"
          />
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog 
        open={showCreateDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingMaterial ? 'Edit Test Material' : 'Create Test Material'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Content Type</InputLabel>
                <Select
                  value={formData.content_type}
                  onChange={(e) => setFormData({ ...formData, content_type: e.target.value })}
                >
                  <MenuItem value="text">Text</MenuItem>
                  <MenuItem value="json">JSON</MenuItem>
                  <MenuItem value="image">Image</MenuItem>
                  <MenuItem value="audio">Audio</MenuItem>
                  <MenuItem value="video">Video</MenuItem>
                  <MenuItem value="pdf">PDF</MenuItem>
                  <MenuItem value="binary">File (Any Type)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              {['text', 'json'].includes(formData.content_type) ? (
                <TextField
                  fullWidth
                  label="Content"
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  multiline
                  rows={10}
                  required
                  placeholder={
                    formData.content_type === 'json' 
                      ? '{\n  "key": "value"\n}' 
                      : 'Enter your test content here...'
                  }
                  sx={{ fontFamily: formData.content_type === 'json' ? 'monospace' : 'inherit' }}
                />
              ) : (
                <Box>
                  <input
                    accept={
                      formData.content_type === 'image' ? 'image/*' :
                      formData.content_type === 'audio' ? 'audio/*' :
                      formData.content_type === 'video' ? 'video/*' :
                      formData.content_type === 'pdf' ? 'application/pdf' :
                      '*/*'
                    }
                    style={{ display: 'none' }}
                    id="test-material-file-upload"
                    type="file"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        if (file.size > MAX_FILE_SIZE_BYTES) {
                          setError(`File size exceeds maximum allowed size of ${MAX_FILE_SIZE_DISPLAY}`);
                          e.target.value = '';
                          return;
                        }
                        setFormData({ ...formData, file });
                        setError(null);
                      }
                    }}
                  />
                  <label htmlFor="test-material-file-upload">
                    <Box sx={{ 
                      border: '2px dashed #ccc', 
                      borderRadius: 2, 
                      p: 3, 
                      textAlign: 'center',
                      backgroundColor: '#f5f5f5',
                      cursor: 'pointer',
                      '&:hover': {
                        borderColor: '#999',
                        backgroundColor: '#efefef'
                      }
                    }}>
                      {formData.file ? (
                        <Box>
                          <Typography variant="body1" color="primary">
                            {formData.file.name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {(formData.file.size / 1024 / 1024).toFixed(2)} MB
                          </Typography>
                        </Box>
                      ) : (
                        <Box>
                          <Typography variant="body2" color="textSecondary">
                            Click to upload {formData.content_type} file
                          </Typography>
                          <Typography variant="caption" color="textSecondary" display="block">
                            {formData.content_type === 'image' ? 'JPG, PNG, GIF, etc.' :
                             formData.content_type === 'audio' ? 'MP3, WAV, etc.' :
                             formData.content_type === 'video' ? 'MP4, AVI, etc.' :
                             formData.content_type === 'pdf' ? 'PDF files only' :
                             'Any file type'}
                          </Typography>
                          <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
                            Max size: {MAX_FILE_SIZE_DISPLAY}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </label>
                </Box>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={editingMaterial ? handleUpdate : handleCreate}
            variant="contained"
            disabled={
              !formData.name || 
              (['text', 'json'].includes(formData.content_type) ? !formData.content : !formData.file)
            }
          >
            {editingMaterial ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
